(ns arthur.follow.page
  "A party's pointers and panel scroll, anywhere on the page.

  A POINTER IS WHAT IT POINTS AT, NOT WHERE IT IS. Two windows are never laid
  out alike — another size, a pane dragged wider, the stage zoomed — so a pixel
  lands on something else. What goes out instead is the element under the
  pointer, as a path down from <body>, and where it is inside each element
  along the way; a mate finds the same element on their page and puts the
  pointer at the same place in it. Over a button, it is over their button.
  Google Docs anchors a caret to the text rather than to the screen, and
  Liveblocks recommends this for cursors on a responsive page; this is that,
  for a page of panels.

  IN AN SVG — the stage, the timeline's curves — the point is in the SVG's own
  units instead, so a pointer on the drawing stays on the drawing at any zoom
  and scroll.

  A path that only partly resolves — a menu open there and not here — falls
  back to the deepest element both pages have, and the pointer shows dimmed.
  One scrolled out of a pane waits at its edge.

  Plain DOM and no React: drawn once a frame, and only while one is showing.")

;; ---------------------------------------------------------------------------
;; addresses

(defn- classes [^js el] (.getAttribute el "class"))
(defn- kind [^js el] [(.-localName el) (.. el -classList (item 0))])

(defn- label
  "What `el` is called, if anything says: an id, an aria-label, a field's name.
  Written for people and screen readers, so it says WHICH thing it is — the
  inspector, not the second `section.pane` — and it does not change when the
  layout does."
  [^js el]
  (some (fn [a] (some->> (.getAttribute el a) not-empty (str a "="))) ["id" "aria-label" "name"]))

(defn- nth-like
  "Which of `el`'s siblings that `same?` says are like it, it is."
  [^js el same?]
  (count (take-while #(not (identical? % el))
                     (filter same? (array-seq (.. el -parentElement -children))))))

(defn- step
  "`el` as one step of a path, three ways from surest to least: by what it is
  called, among its siblings called that; by its tag and classes; and by its tag
  and FIRST class, because the rest come and go with state — `on`, `selected` —
  and a row lit on one page is still the same row on the other."
  [^js el]
  (let [tag (.-localName el) cls (classes el) k (kind el) l (label el)]
    [tag cls
     (nth-like el #(and (= tag (.-localName %)) (= cls (classes %))))
     (nth-like el #(= k (kind %)))
     l (when l (nth-like el #(= l (label %))))]))

(defn- child [^js parent [tag cls n m l i]]
  (let [kids (array-seq (.-children parent))
        k    [tag (not-empty (first (some-> cls (.split " "))))]]
    (or (when l (nth (filter #(= l (label %)) kids) i nil))
        (nth (filter #(and (= tag (.-localName %)) (= cls (classes %))) kids) n nil)
        (nth (filter #(= k (kind %)) kids) m nil))))

(defn- lineage
  "The elements from below <body> down to `el`."
  [^js el]
  (->> (iterate #(.-parentElement ^js %) el)
       (take-while #(and % (not (identical? % js/document.body))))
       reverse))

(defn- svg-root [^js el]
  (when (instance? js/SVGElement el)
    (last (take-while some? (iterate #(.-ownerSVGElement ^js %) el)))))

(defn- round [x] (/ (js/Math.round (* 1000 x)) 1000))

(defn- through [^js svg m x y]
  (let [p (.matrixTransform (js/DOMPoint. x y) m)] [(round (.-x p)) (round (.-y p))]))

(defn- address
  "Where client point `x` `y` is, as a mate's page can find it."
  [x y]
  (when-let [hit (.elementFromPoint js/document x y)]
    (let [svg (svg-root hit)
          els (lineage (or svg hit))]
      {:path (mapv step els)
       :at   (mapv (fn [^js e]
                     (let [r (.getBoundingClientRect e)]
                       [(round (/ (- x (.-left r)) (max 1 (.-width r))))
                        (round (/ (- y (.-top r)) (max 1 (.-height r))))]))
                   els)
       :page [(round (/ x (.-innerWidth js/window))) (round (/ y (.-innerHeight js/window)))]
       :svg  (some-> svg .getScreenCTM .inverse (as-> m (through svg m x y)))})))

(defn- find-path
  "The elements `path` names on this page, <body> first, as deep as they go."
  [path]
  (reduce (fn [found s]
            (if-let [c (child (peek found) s)] (conj found c) (reduced found)))
          [js/document.body] path))

;; ---------------------------------------------------------------------------
;; drawing them

(defonce ^:private shown (atom {}))       ; cid → {:msg :found :clip :xy :el}
(defonce ^:private ticking (atom nil))    ; [:frame id] or [:idle id]

(defn- layer []
  (or (.getElementById js/document "follow-layer")
      (doto (.createElement js/document "div")
        (set! -id "follow-layer")
        (->> (.appendChild js/document.body)))))

(defn- arrow [{:keys [who colour]}]
  (doto (.createElement js/document "div")
    (set! -className "follow-pointer")
    (-> .-style (.setProperty "--who" colour))
    (set! -innerHTML (str "<i></i><svg viewBox='0 0 11 15'>"
                          "<path d='M.5 .5V13.5L3.9 10.4 6.1 15.1 8.1 14.2 5.9 9.6H10.5z'/></svg>"
                          "<span></span>"))
    (-> (.querySelector "span") (set! -textContent who))
    (->> (.appendChild (layer)))))

(defn- clips
  "The ancestors of the last of `found` that cut off what overflows them."
  [found]
  (filter #(not= "visible" (.-overflow (js/getComputedStyle %))) (rest (pop found))))

(defn- resolved [{:keys [path] :as m}]
  (let [found (find-path path)] (assoc m :found found :clip (clips found))))

(defn- target
  "Where a pointer goes on this page, and whether it is lost (only part of its
  path is here) or away (scrolled out of view, so held at the edge)."
  [{:keys [path at page svg found clip]}]
  (let [d  (dec (count found))
        ^js el (peek found)
        [x y] (cond
                (and svg (= d (count path)))
                (if-let [m (.getScreenCTM el)] (through el m (svg 0) (svg 1)) [-99 -99])
                (pos? d)
                (let [r (.getBoundingClientRect el) [fx fy] (nth at (dec d))]
                  [(+ (.-left r) (* fx (.-width r))) (+ (.-top r) (* fy (.-height r)))])
                :else [(* (page 0) (.-innerWidth js/window)) (* (page 1) (.-innerHeight js/window))])
        [l t r b] (reduce (fn [[l t r b] ^js c]
                            (let [q (.getBoundingClientRect c)]
                              [(max l (.-left q)) (max t (.-top q))
                               (min r (.-right q)) (min b (.-bottom q))]))
                          [0 0 (.-innerWidth js/window) (.-innerHeight js/window)] clip)
        cx (max l (min r x)) cy (max t (min b y))]
    {:xy [cx cy] :lost? (< d (count path)) :away? (not= [x y] [cx cy])}))

(declare frame!)

(defn- tick!
  "Draw again: next frame while a pointer is on its way, and four times a
  second once they have all arrived — only to notice a layout that moved
  under one, a pane opened or a list scrolled, which no message announces."
  [moving?]
  (reset! ticking (cond (empty? @shown) nil
                        moving? [:frame (js/requestAnimationFrame frame!)]
                        :else   [:idle (js/setTimeout frame! 250)])))

(defn- frame! [_]
  (tick!
   (reduce
    (fn [moving? [cid {:keys [found xy ^js el] :as s}]]
      (let [s (cond-> s (not (.-isConnected ^js (peek found))) resolved)
            {[tx ty] :xy :keys [lost? away?]} (target s)
            ;; Halfway there each frame: messages come every 40ms, frames every
            ;; 16, and a pointer that jumps between them looks like it stutters.
            ;; Within half a pixel, there.
            [x y] (if (and xy (< 0.5 (js/Math.hypot (- tx (xy 0)) (- ty (xy 1)))))
                    [(+ (xy 0) (* 0.5 (- tx (xy 0)))) (+ (xy 1) (* 0.5 (- ty (xy 1))))]
                    [tx ty])]
        (when (not= xy [x y])
          (set! (.. el -style -transform) (str "translate(" x "px," y "px)")))
        (.toggle (.-classList el) "lost" lost?)
        (.toggle (.-classList el) "away" away?)
        (swap! shown assoc cid (assoc s :xy [x y]))
        (or moving? (not= [x y] [tx ty]))))
    false @shown)))

(defn gone! [cid]
  (some-> (get-in @shown [cid :el]) .remove)
  (swap! shown dissoc cid))

(defn clear! [] (run! gone! (keys @shown)))

(defn seen!
  "A mate's pointer message, `{:path :at :page :svg :clicks}` — or no path,
  when it has left their window."
  [cid who {:keys [path clicks] :as m}]
  (if-not path
    (gone! cid)
    (let [was (get @shown cid)
          el  (or (:el was) (arrow who))]
      ;; A press, as a ring: the one thing you cannot tell from the pointer alone.
      (when (and was (not= clicks (get-in was [:msg :clicks])))
        (.animate (.querySelector el "i")
                  #js [#js {:transform "scale(.3)" :opacity 1} #js {:transform "scale(1.6)" :opacity 0}]
                  450))
      (swap! shown assoc cid (-> (resolved m) (assoc :msg m :el el :xy (:xy was))))
      (let [[how id] @ticking]
        (when-not (= :frame how)
          (some-> id js/clearTimeout)
          (tick! true))))))

;; ---------------------------------------------------------------------------
;; scroll

(defonce ^:private applied (js/WeakMap.))

(defn- scrolled-to [^js el] [(.-scrollTop el) (.-scrollLeft el)])

(defn scrolled!
  "A mate scrolled a panel. Only on a whole path: half a path is some other
  panel. In pixels, because a list's rows are the same height on both pages."
  [{:keys [path top left]}]
  (let [found (find-path path)]
    (when (= (count found) (inc (count path)))
      (let [^js el (peek found)]
        (set! (.-scrollTop el) top)
        (set! (.-scrollLeft el) left)
        (.set applied el (scrolled-to el))))))

;; ---------------------------------------------------------------------------

(defn every
  "`f` with the latest arguments, at most once every `gap` ms: the first at
  once, and the last always, so a mate sees where a move ENDED."
  [gap f]
  (let [timer (atom nil) pending (atom nil)]
    (fn [& args]
      (reset! pending args)
      (when-not @timer
        (apply f args)
        (reset! pending nil)
        (reset! timer (js/setTimeout (fn self []
                                       (if-let [a @pending]
                                         (do (reset! pending nil) (apply f a)
                                             (reset! timer (js/setTimeout self gap)))
                                         (reset! timer nil)))
                                     gap))))))

(defonce ^:private installed? (atom false))

(defn install!
  "Watch the page for a party: `sharing` says whether there is one, and what
  tab we are on; `send!` tells it."
  [{:keys [send! sharing]}]
  (when-not @installed?
    (reset! installed? true)
    (let [clicks (atom 0)
          point! (every 40 (fn [x y]
                             (when-let [{:keys [open]} (sharing)]
                               (send! (merge {:kind "cursor" :open open :clicks @clicks}
                                             (when x (address x y)))))))
          scroll! (every 60 (fn [^js el]
                              (when (:scroll? (sharing))
                                (let [[top left] (scrolled-to el)]
                                  (send! {:kind "scroll" :path (mapv step (lineage el))
                                          :top top :left left})))))
          on (fn [target kind f] (.addEventListener target kind f #js {:capture true :passive true}))]
      (on js/document "pointermove" (fn [^js e] (when (.-isPrimary e) (point! (.-clientX e) (.-clientY e)))))
      (on js/document "pointerdown" (fn [^js e]
                                      (when (.-isPrimary e)
                                        (swap! clicks inc)
                                        (point! (.-clientX e) (.-clientY e)))))
      (on js/document.documentElement "mouseleave" (fn [_] (point! nil nil)))
      (on js/document "visibilitychange" (fn [_] (when (.-hidden js/document) (point! nil nil))))
      (on js/document "scroll"
          (fn [^js e]
            (let [el (.-target e)]
              ;; Not the stage's: how far in it is scrolled depends on its zoom,
              ;; which is each person's own. A pointer on it is in stage units.
              (when (and (instance? js/Element el) (not (.closest el ".stage-area")))
                (if (= (.get applied el) (scrolled-to el))
                  (.delete applied el)                ; ours, from a mate's
                  (scroll! el)))))))))
